import type { LiveBreakHostDetail, LiveStreamHostDetail } from "@hobbyshopify/backend-client";
import { LiveRefused } from "@hobbyshopify/backend-client";
import { getError } from "operation-result.js";
import { type Disposable, SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  addSegments,
  endStream,
  loadBreakHostDetail,
  loadStreamHostDetail,
  loadStreamViewers,
  provisionIngest,
  syncRecording,
  syncStream,
  takeOverStream,
} from "../../core/ops/live-ops";
import { errorText } from "../../core/primitives/errors";
import { streamIsOver } from "../../core/primitives/live";
import { busyForm, failedForm, failedLoad, idleForm, loaded, loading } from "../../core/primitives/load-status";
import { Loader } from "../../core/primitives/loader";
import { stripeConnection } from "../../core/primitives/onboarding";
import { currentBreak, streamTotals } from "../../core/primitives/stream-cockpit";
import {
  connectBreakRealtime,
  connectStreamRealtime,
  type LiveRealtimeEvent,
  type StreamRealtime,
} from "../../core/services/live-realtime";
import { LineupPickerController } from "../../sections/lineup-picker/lineup-picker-controller";
import { type ChatTab, type CockpitDialog, LiveStreamActions, LiveStreamState } from "./live-stream-model";
import { CockpitChatController } from "./sections/cockpit-chat/cockpit-chat-controller";
import { LineupController } from "./sections/lineup/lineup-controller";
import { StageController } from "./sections/stage/stage-controller";
import { StreamCycleController } from "./sections/stream-cycle/stream-cycle-controller";
import { ViewerPreviewController } from "./sections/viewer-preview/viewer-preview-controller";

export type LiveStreamDeps = Pick<
  AppDeps,
  | "client"
  | "session"
  | "authHandler"
  | "storeRepository"
  | "locationsRepository"
  | "profileRepository"
  | "broadcastStudio"
  | "shareLinks"
>;

export interface LiveStreamParams {
  streamId: string;
}

export interface LiveStreamRouting {
  toStreams(): void;
}

/**
 * Ask the provider whether video is really arriving, until it says yes: brisk
 * while this device is publishing and a flip is imminent, slow otherwise.
 */
export const STREAM_SYNC_ON_AIR_MS = 3_000;
export const STREAM_SYNC_IDLE_MS = 15_000;

/**
 * The cockpit: everything the host does over the camera, as one screen. The
 * camera itself belongs to the studio, mounted above the router, so leaving
 * this page does not take the stream off air; this only says which show the
 * camera is for and mirrors its state.
 */
export class LiveStreamController extends SubtreeController implements LiveStreamActions {
  readonly state: LiveStreamState;
  readonly chat: CockpitChatController;
  readonly lineup: LineupController;
  readonly picker: LineupPickerController;
  readonly cycle: StreamCycleController;
  readonly viewer: ViewerPreviewController;
  readonly stage: StageController;
  readonly #detail: Loader<string, LiveStreamHostDetail>;
  readonly #breakDetail: Loader<string, LiveBreakHostDetail>;
  /** One realtime connection for the whole cockpit: a second one would count the host in their own audience. */
  readonly #realtime: StreamRealtime;
  #breakRealtime: Disposable | null = null;
  #syncPoll: ReturnType<typeof setInterval> | null = null;
  #syncPollMs: number | null = null;
  #currentSegmentId: string | null | undefined;

  constructor(
    readonly deps: LiveStreamDeps,
    params: LiveStreamParams,
    readonly routing: LiveStreamRouting,
  ) {
    super();
    const { streamId } = params;
    this.state = new LiveStreamState(streamId);
    this.subtree.put(LiveStreamState, this.state);
    this.subtree.put(LiveStreamActions, this);
    this.sync(() => {
      const stream = this.state.stream.value;
      this.state.share.value =
        stream && stream.status !== "CANCELLED"
          ? deps.shareLinks.stream(stream, this.state.store.value?.name ?? null)
          : null;
    }, [this.state.stream, this.state.store]);

    this.#detail = this.own(
      new Loader({
        status: this.state.status,
        fetch: (id: string) => loadStreamHostDetail({ ...deps, streamId: id }),
        loaded: (detail) => {
          this.state.stream.value = detail.stream;
          this.state.breaks.value = detail.breaks;
          this.state.segments.value = detail.stream.segments;
          this.state.isHost.value = detail.isHost;
        },
      }),
    );
    this.#breakDetail = this.own(
      new Loader({
        status: this.state.slotsStatus,
        fetch: (breakId: string) => loadBreakHostDetail({ ...deps, breakId }),
        loaded: (detail, breakId) => {
          if (this.state.shownBreakId.value === breakId) this.state.slots.value = detail.slots;
        },
      }),
    );

    this.chat = this.own(new CockpitChatController(this.subtree, deps, { streamId }));
    this.picker = this.own(
      new LineupPickerController(this.subtree, deps, {
        commit: async (items) => {
          if (items.length === 0) return null;
          const r = await addSegments({ ...deps, streamId, items });
          if (!r.ok) return errorText(r.errors);
          void this.reload();
          return null;
        },
      }),
    );
    this.lineup = this.own(
      new LineupController(this.subtree, deps, {
        streamId,
        segments: this.state.segments,
        onChanged: () => void this.reload(),
        addMore: () => this.picker.show("add"),
      }),
    );
    this.cycle = this.own(
      new StreamCycleController(this.subtree, deps, {
        streamId,
        stream: this.state.stream,
        segments: this.state.segments,
        breaks: this.state.breaks,
        buyers: this.state.buyers,
        onAir: this.state.onAir,
        isHost: this.state.isHost,
        goLive: () => this.goLive(),
        addMore: () => this.picker.show("add"),
        endStream: () => this.openDialog("end"),
        openLineup: () => {
          this.state.lineupOpen.value = true;
        },
        onChanged: () => void this.reload(),
      }),
    );
    this.viewer = this.own(new ViewerPreviewController(this.subtree));
    this.stage = this.own(new StageController(this.subtree, deps, { streamId }));

    this.#realtime = connectStreamRealtime({ ...deps, streamId, onEvent: this.onRealtimeEvent });
    this.autoDispose(() => this.#realtime.dispose());
    this.autoDispose(() => this.#breakRealtime?.dispose());
    this.autoDispose(() => this.#stopSyncPoll());

    this.sync(() => {
      const store = deps.storeRepository.selectedStore;
      this.state.store.value = store;
      this.state.paymentsReady.value = stripeConnection(store)?.status === "ACTIVE";
    }, [deps.storeRepository]);

    this.sync(() => {
      const studio = deps.broadcastStudio;
      this.state.phase.value = studio.phase;
      this.state.cameraError.value = studio.error;
      this.state.cameras.value = studio.cameras;
      this.state.cameraId.value = studio.cameraId;
      this.state.wire.value = studio.wire;
      this.state.interrupted.value = studio.interrupted;
      this.state.reconnecting.value = studio.reconnecting;
      this.state.onAir.value = studio.onAir;
      this.state.hasMic.value = studio.hasMic;
      this.state.micMuted.value = studio.micMuted;
    }, [deps.broadcastStudio]);

    this.sync(() => {
      this.state.watching.value = this.#realtime.watching;
    }, [this.#realtime]);

    // The list follows the count: somebody arriving or leaving reloads it while it is open.
    this.sync(() => {
      if (this.state.chatTab.value === "room") void this.#loadViewers();
    }, [this.state.chatTab, this.state.watching]);

    this.sync(() => {
      const breaks = this.state.breaks.value;
      this.state.over.value = this.state.stream.value ? streamIsOver(this.state.stream.value.status) : false;
      this.state.totals.value = streamTotals(breaks);
    }, [this.state.stream, this.state.breaks]);

    // Which show the camera is for. An ended stream takes it down instead: the
    // page used to say "this stream has ended" over a camera still publishing.
    this.sync(() => this.#presentBroadcast(), [this.state.stream, deps.profileRepository]);

    // A box the host tapped stays shown until the stream moves on: another
    // segment goes on screen, or that box leaves the line-up or is finished.
    this.sync(() => {
      const current = this.state.segments.value.find((s) => s.status === "CURRENT")?.id ?? null;
      if (this.#currentSegmentId !== undefined && current !== this.#currentSegmentId) {
        this.state.pickedBreakId.value = null;
      }
      this.#currentSegmentId = current;
    }, [this.state.segments]);

    this.sync(() => {
      const breaks = this.state.breaks.value;
      const picked = breaks.find((b) => b.id === this.state.pickedBreakId.value);
      const stillShown = picked && picked.status !== "COMPLETED" && picked.status !== "CANCELLED";
      const shown = currentBreak(this.state.segments.value, breaks);
      this.state.shownBreakId.value = (stillShown ? picked.id : null) ?? shown?.id ?? null;
    }, [this.state.breaks, this.state.segments, this.state.pickedBreakId]);

    this.sync(() => {
      const id = this.state.shownBreakId.value;
      this.state.shownBreak.value = this.state.breaks.value.find((b) => b.id === id) ?? null;
    }, [this.state.breaks, this.state.shownBreakId]);

    this.sync(() => this.#followShownBreak(), [this.state.shownBreakId]);

    this.sync(() => {
      this.state.buyers.value = this.state.slots.value.filter((s) => s.buyerName).map((s) => s.buyerName as string);
    }, [this.state.slots]);

    this.sync(() => this.#reconcileSyncPoll(), [this.state.stream, this.state.onAir]);

    void this.reload();
  }

  reload = async (): Promise<void> => {
    await this.#detail.load(this.state.streamId);
  };

  /**
   * What the realtime channels say. Invalidating rather than patching state by
   * hand: the payloads are deltas, and a cockpit that reconstructed totals from
   * them would drift.
   */
  onRealtimeEvent = (event: LiveRealtimeEvent) => {
    if (event.type === "chat.message") {
      void this.chat.reload();
      return;
    }
    if (event.type === "chat.deleted") {
      void this.chat.reload();
      return;
    }
    if (event.type === "stage.queue" || event.type === "stage.invite") {
      void this.stage.reload();
      return;
    }
    void this.reload();
    if (
      (event.type === "slot.updated" || event.type === "break.reveal") &&
      event.breakId === this.state.shownBreakId.value
    ) {
      void this.#breakDetail.load(event.breakId);
    }
  };

  selectChatTab = (tab: ChatTab) => {
    this.state.chatTab.value = tab;
  };

  async #loadViewers(): Promise<void> {
    if (this.state.viewers.value === null) this.state.viewersStatus.value = loading;
    const r = await loadStreamViewers({ ...this.deps, streamId: this.state.streamId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.viewersStatus.value = failedLoad(r.errors);
      return;
    }
    this.state.viewers.value = r.value;
    this.state.viewersStatus.value = loaded;
  }

  toggleLineup = () => {
    this.state.lineupOpen.value = !this.state.lineupOpen.value;
  };

  togglePreview = () => {
    this.state.preview.value = !this.state.preview.value;
  };

  openDialog = (dialog: CockpitDialog | null) => {
    this.state.end.value = idleForm;
    this.state.endCode.value = null;
    this.state.dialog.value = dialog;
  };

  pickBreak = (breakId: string) => {
    this.state.pickedBreakId.value = breakId;
  };

  goLive = () => {
    this.deps.broadcastStudio.start();
  };

  stopCamera = () => {
    this.deps.broadcastStudio.stop();
  };

  restartCamera = () => {
    this.deps.broadcastStudio.restartCamera();
  };

  switchCamera = (deviceId: string) => {
    this.deps.broadcastStudio.switchCamera(deviceId);
  };

  toggleMic = () => {
    this.deps.broadcastStudio.toggleMic();
  };

  endStream = async (finishRipping?: boolean) => {
    if (this.state.end.value.busy) return;
    this.state.end.value = busyForm;
    this.state.endCode.value = null;
    const r = await endStream({ ...this.deps, streamId: this.state.streamId, finishRipping });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.end.value = failedForm(r.errors);
      this.state.endCode.value = getError(r, LiveRefused)?.code ?? null;
      return;
    }
    this.state.end.value = idleForm;
    this.state.dialog.value = null;
    await this.reload();
  };

  checkRecording = async () => {
    if (this.state.recording.value.busy) return;
    this.state.recording.value = busyForm;
    const r = await syncRecording({ ...this.deps, streamId: this.state.streamId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.recording.value = failedForm(r.errors);
      return;
    }
    this.state.recording.value = idleForm;
    this.state.recordingResult.value = r.value;
    await this.reload();
  };

  getStreamKey = async () => {
    if (this.state.encoder.value.busy) return;
    this.state.encoder.value = busyForm;
    const r = await provisionIngest({ ...this.deps, streamId: this.state.streamId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.encoder.value = failedForm(r.errors);
      return;
    }
    this.state.encoder.value = idleForm;
    this.state.ingest.value = r.value;
  };

  takeOver = async () => {
    if (this.state.takeOver.value.busy) return;
    this.state.takeOver.value = busyForm;
    const r = await takeOverStream({ ...this.deps, streamId: this.state.streamId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.takeOver.value = failedForm(r.errors);
      return;
    }
    this.state.takeOver.value = idleForm;
    await this.reload();
  };

  #presentBroadcast(): void {
    const stream = this.state.stream.value;
    if (!stream) return;
    if (streamIsOver(stream.status)) {
      this.deps.broadcastStudio.dismiss(stream.id);
      return;
    }
    // The stream is already on and this is its host, so a phone that died and
    // came back should be broadcasting again without being asked.
    const me = this.deps.profileRepository.profile?.id;
    const resumeAsHost = stream.status === "LIVE" && Boolean(stream.host) && stream.host?.id === me;
    this.deps.broadcastStudio.present({ streamId: stream.id, title: stream.title, resumeAsHost });
  }

  /** The grid and its channel follow the shown break; a 300-spot grid stays off the stream channel. */
  #followShownBreak(): void {
    const breakId = this.state.shownBreakId.value;
    this.#breakRealtime?.dispose();
    this.#breakRealtime = null;
    if (!breakId) {
      this.#breakDetail.reset();
      this.state.slots.value = [];
      return;
    }
    void this.#breakDetail.load(breakId);
    this.#breakRealtime = connectBreakRealtime({
      ...this.deps,
      streamId: this.state.streamId,
      breakId,
      onEvent: this.onRealtimeEvent,
    });
  }

  /**
   * The stream flips to LIVE from the provider's webhook, which cannot reach a
   * backend the provider cannot address. This is the same question asked over
   * the API rather than a page assuming it went live.
   */
  #reconcileSyncPoll(): void {
    const scheduled = this.state.stream.value?.status === "SCHEDULED";
    const wanted = scheduled ? (this.state.onAir.value ? STREAM_SYNC_ON_AIR_MS : STREAM_SYNC_IDLE_MS) : null;
    if (wanted === this.#syncPollMs) return;
    this.#stopSyncPoll();
    this.#syncPollMs = wanted;
    if (wanted === null) return;
    void this.#askStream();
    this.#syncPoll = setInterval(() => void this.#askStream(), wanted);
  }

  #stopSyncPoll(): void {
    if (this.#syncPoll) clearInterval(this.#syncPoll);
    this.#syncPoll = null;
    this.#syncPollMs = null;
  }

  async #askStream(): Promise<void> {
    // The webhook remains the normal path, so neither a refusal nor a thrown
    // transport error is news here — only a confirmed `live` is.
    const r = await syncStream({ ...this.deps, streamId: this.state.streamId }).catch(() => null);
    if (this.isDisposed) return;
    if (r?.ok && r.value.live) void this.reload();
  }
}
